import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod/v3';
import { AnimatedCubeIcon } from '@/components/animated/animated-cube-icon';
import { FormError } from '@/components/ui/form-error';
import { api, isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError(null);
      setSuccess(false);

      await api.post('/auth/forgot-password', { email: data.email });

      setSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      if (isApiError(err)) {
        setError(err.response?.data.message || 'Erro ao requisitar nova senha');
      } else {
        setError('Erro ao requisitar nova senha. Tente novamente.');
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
      <div className="flex items-center justify-center w-full max-w-6xl h-[40rem] lg:h-[40rem] max-h-[90vh] p-20 rounded-xl border border-gray-200 bg-white text-gray-900 shadow">
        <div className="w-full flex flex-col lg:flex-row">
          <div className="hidden lg:flex flex-1 items-center justify-center relative">
            <div
              className={cn(
                'absolute inset-0 rounded-l-xl',
                '[background-size:40px_40px]',
                '[background-image:linear-gradient(to_right,#d4d4d8_1px,transparent_1px),linear-gradient(to_bottom,#d4d4d8_1px,transparent_1px)]',
              )}
            />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] rounded-l-xl" />

            <div className="relative text-center">
              <AnimatedCubeIcon size={60} className="mb-8 mx-auto" />
              <p className="bg-gradient-to-b from-gray-300 to-black bg-clip-text text-4xl font-bold text-transparent lg:text-5xl">
                BCUBE
              </p>
              <p className="mt-4 text-gray-600">Recupere o acesso à sua conta</p>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-md">
              <div className="text-center px-0">
                <div className="lg:hidden mb-6">
                  <AnimatedCubeIcon size={40} className="mx-auto" />
                </div>

                <h1 className="text-2xl font-bold text-gray-900">Esqueceu sua senha?</h1>
                <p className="mt-2 text-center text-sm text-gray-600">
                  Digite seu email para receber as instruções de recuperação
                </p>
              </div>

              <div className="px-0 mt-4">
                {success && (
                  <output className="mb-6 block rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-600">
                    Nova senha requisitada com sucesso. Verifique seu email.
                  </output>
                )}

                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Insira o e-mail do seu usuário"
                        {...register('email')}
                        className={cn(
                          'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
                          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                          'placeholder:text-gray-400',
                          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          errors.email ? 'border-red-500' : 'border-gray-300',
                        )}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <FormError error={error} onDismiss={() => setError(null)} />
                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting || success}
                      className={cn(
                        'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium',
                        'bg-gray-900 text-white shadow',
                        'hover:bg-gray-800',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                        'disabled:pointer-events-none disabled:opacity-50',
                      )}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        'Requisitar nova senha'
                      )}
                    </button>
                  </div>

                  <div className="mt-4 text-center">
                    <Link to="/login" className="text-sm text-blue-600 hover:underline">
                      Voltar para o login
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
